import type { PermissionChecker } from '../permissions/engine';
import type { EffectivePermissions, RawGrant } from '../permissions/types';

/** JWT claims the legacy admin already relies on — the wire contract is unchanged. */
export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: string;
}

/**
 * Where a tenant's data lives, as the TOKEN advertises it (`custom:tenant_type`).
 *
 * ADVISORY, always. The placement resolver on the service side is the source of truth
 * (docs/standards/jwt-contract.md, backend standard rules 5–7); this claim exists so the console
 * can SHOW an operator which world they are in, and it is never what selects a database.
 * `unknown` is a real state: a token minted before the mapper existed carries no such claim, and
 * inventing POOL for it would be a lie in the one place an operator would believe it.
 */
export type TenantPlacement = 'POOL' | 'SILO' | 'unknown';

export interface Tenant {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  /**
   * True when `name` came from the token's `custom:tenant_name` claim (the legacy session's
   * hand-over carries it). Such a name wins over an environment's presented name
   * (`chrome.demoTenants`, SDD-022 D-5); absent or false, `name` is only the slug.
   */
  readonly nameFromClaim?: boolean;
  /** `custom:tenant_type` — advisory (see {@link TenantPlacement}). */
  readonly placement: TenantPlacement;
  /** `custom:tenant_location` — advisory: the pool or silo the resolver is expected to pick. */
  readonly location: string | null;
}

export interface AuthSession {
  readonly user: AuthUser;
  readonly tenant: Tenant;
  /** `tokenResponse.permissions`, verbatim, exactly as the auth service delivers it today. */
  readonly grants: readonly RawGrant[];
  readonly accessToken: string;
  readonly expiresAt: number;
}

/**
 * OIDC/PKCE lives behind this interface so the mock adapter and the real one are
 * interchangeable — dev, Storybook, tests and BDD run the mock; every deployed environment runs
 * the real one. There is deliberately no `kind: 'cognito'`: the console talks to an OIDC issuer,
 * Keycloak locally and Cognito in EKS, and knowing which would be knowledge it has no use for
 * (decisions T-3/T-4).
 */
export interface AuthAdapter {
  /**
   * `legacy-session` is the transition's third adapter (SDD-012, U-6'): the session is handed
   * over by the legacy admin that opened this window, through `postMessage`. Deleting it when the
   * legacy is gone is deleting one file and one enum member.
   */
  readonly kind: 'mock' | 'oidc' | 'legacy-session';
  /**
   * Starts a session.
   *
   * Resolves `null` when an INTERACTIVE redirect has begun: the authorization-code flow leaves
   * this document, so there is no session to hand back and the caller must not treat that as a
   * failure. The mock adapter always resolves a session, because nothing navigates.
   */
  signIn: (hint?: string) => Promise<AuthSession | null>;
  signOut: () => Promise<void>;
  /** Restores a session on reload; resolves null when there is none. */
  restore: () => Promise<AuthSession | null>;
  /** Refresh ahead of expiry (legacy uses a 10 s buffer; kept). */
  refresh: () => Promise<AuthSession>;
  /**
   * The token the SDK clients quote, refreshed if it is about to expire.
   *
   * This is the seam between auth and data: `createEisClient({ tokenProvider })` calls it per
   * request, so a silent refresh is picked up without rebuilding a client. Null means "no
   * session" — an unauthenticated request, not an error.
   */
  getAccessToken: () => Promise<string | null>;
  /**
   * The SDK's `sessionRefresher` (SDD-019 S-6, S-11): refreshes the session NOW — after a request
   * came back 401/403 — and resolves the new access token, or null when there is no session any
   * more. The SDK does not know which backend this calls: authservice's `session:refresh` for
   * `oidc`, the legacy `/authservice/v1/refresh-token` for `legacy-session`. Absent: nothing to
   * refresh (the mock adapter), and the SDK retries nothing.
   */
  readonly sessionRefresher?: () => Promise<string | null>;
  /** What the SDK calls when that refresh failed: this window has no session any more. */
  readonly onSessionLost?: () => void;
  /** The adapter's own refresh endpoints, which the SDK must never retry. */
  readonly isSessionEndpoint?: (url: string) => boolean;
}

export interface AuthContextValue extends PermissionChecker {
  readonly status: 'loading' | 'authenticated' | 'unauthenticated';
  readonly user: AuthUser | null;
  readonly tenant: Tenant | null;
  /** Raw grants → normalization result → effective set: the permission inspector's input. */
  readonly permissions: EffectivePermissions | null;
  signIn: (hint?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const TOKEN_REFRESH_BUFFER_MS = 10_000;
